import { useEffect, useState, useCallback } from 'react';
import './Toast.css';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
  id: number;
  text: string;
  type: ToastType;
}

interface ToastProps {
  messages: ToastMessage[];
  onRemove: (id: number) => void;
}

function ToastItem({ msg, onRemove }: { msg: ToastMessage; onRemove: (id: number) => void }) {
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExiting(true);
      setTimeout(() => onRemove(msg.id), 250);
    }, 3000); // 3.0s total time visible
    return () => clearTimeout(timer);
  }, [msg.id, onRemove]);

  const icon = msg.type === 'success' ? '✅' : msg.type === 'error' ? '❌' : 'ℹ️';

  return (
    <div className={`toast toast--${msg.type} ${exiting ? 'toast--exit' : ''}`}>
      <span className="toast__icon">{icon}</span>
      <span className="toast__text">{msg.text}</span>
      <button
        className="toast__close"
        onClick={() => { setExiting(true); setTimeout(() => onRemove(msg.id), 250); }}
        aria-label="Fechar"
      >×</button>
    </div>
  );
}

export function ToastContainer({ messages, onRemove }: ToastProps) {
  return (
    <div className="toast-container">
      {messages.map(msg => (
        <ToastItem key={msg.id} msg={msg} onRemove={onRemove} />
      ))}
    </div>
  );
}

// ── Hook para usar toast facilmente ──────────────────────────────────────────

let _idCounter = 0;

export function useToast() {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const toast = {
    success: (text: string) =>
      setMessages(prev => [...prev, { id: ++_idCounter, text, type: 'success' }]),
    error: (text: string) =>
      setMessages(prev => [...prev, { id: ++_idCounter, text, type: 'error' }]),
    info: (text: string) =>
      setMessages(prev => [...prev, { id: ++_idCounter, text, type: 'info' }]),
  };

  const remove = useCallback((id: number) => {
    setMessages(prev => prev.filter(m => m.id !== id));
  }, []);

  return { messages, toast, remove };
}
